import React, { createContext, useState } from "react";
import ReactGA from "react-ga";
import Axios from "../utils/Axios";
import i18n from "../i18n/i18n";

interface AuthInterface {
    authenticated: boolean;
    authenticate: (username: string, password: string) => void;
    authenticatePasswordless: (username: string, queryStringLogin?: boolean) => void;
    checkAuthentication: () => void;
    logOut: () => void;
    register: (payload: any) => Promise<any>;
    setAdmin: (admin: boolean) => void;
    isLoading: boolean;
    isAdmin: boolean;
    isAutoLogin: boolean;
}

export const AuthenticationContext = createContext<AuthInterface>({
    authenticated: false,
    isLoading: false,
    isAdmin: false,
    isAutoLogin: false,
    authenticate: () => {
        throw new Error('autheticate() method not implemented');
    },
    authenticatePasswordless: () => {
        throw new Error('authenticatePasswordless() method not implemented');
    },
    checkAuthentication: () => {
        throw new Error('checkAuthentication() method not implemented');
    },
    logOut: () => {
        throw new Error('logOut() method not implemented');
    },
    register: () => {
        throw new Error('register() method not implemented');
    },
    setAdmin: () => {
        throw new Error('setAdmin() method not implemented');
    },
});

function isTokenValid() {
    const expiresAt = localStorage.getItem('expires_at');
    if (expiresAt === null) return false;

    const timeToCheck = new Date(Number(expiresAt) * 1000);
    const tokenValid = (timeToCheck > new Date());
    return tokenValid;
}

function isUserAdmin() {
    const isAdmin = localStorage.getItem('is_admin');
    if (isAdmin === null) return false;
    return isAdmin === 'true';
}

function isAuthAutoLogin() {
    const isAutoLogin = localStorage.getItem('autoLogin');
    if (isAutoLogin === null) return false;
    return isAutoLogin === 'true';
}

export const AuthenticationProvider: React.FC = ({children}) => {

    const [authenticated, setAuthenticated] = useState(isTokenValid());
    const [isLoading, setLoading] = useState(false);
    const [isAdmin, setIsAdmin] = useState(isUserAdmin());
    const [isAutoLogin, setIsAutoLogin] = useState(isAuthAutoLogin());

    const register = (payload: any) => {
        return Axios.post('/user-pre-regs', payload);
    }

    const trackSetAuthenticated = (value:boolean) => {
        setAuthenticated(value)
        if (value) {
            ReactGA.event({
                category: 'User',
                action: 'log in',
                label: 'OK'
            })
        }
    }

    const authenticate = (username: string, password: string) => {
        setLoading(true);
        Axios.post('/token', {username: username, password: password}).then((res) => {
            localStorage.setItem('access_token', res.data.access);
            localStorage.setItem('refresh_token', res.data.refresh);
            localStorage.setItem('expires_at', res.data.expires_at);
            localStorage.setItem('is_admin', res.data.is_admin);
            localStorage.removeItem('autoLogin');
            if (res.data.language !== "") {
                i18n.changeLanguage(res.data.language.toLowerCase());
            }
            setLoading(false);
            trackSetAuthenticated(true);
            setIsAdmin(res.data.is_admin);
        }).catch((error) => {
            setLoading(false);
            trackSetAuthenticated(false);
        });
    }

    const setAdmin = (admin: boolean) => {
        setIsAdmin(admin);
    }

    const authenticatePasswordless = (username: string, queryStringLogin = false) => {
        setLoading(true);
        Axios.post('/token/passwordless', {username: username}).then((res) => {
            localStorage.setItem('access_token', res.data.access);
            if (!queryStringLogin) {
                localStorage.setItem('refresh_token', res.data.refresh);
            }
            localStorage.setItem('expires_at', res.data.expires_at);
            localStorage.setItem('autoLogin', 'true');
            setIsAutoLogin(true);
            setLoading(false);
            trackSetAuthenticated(true);
            setIsAdmin(res.data.is_admin);
            localStorage.setItem('is_admin', res.data.is_admin);
        }).catch((error) => {
            setLoading(false);
            trackSetAuthenticated(false);
        });
    }

    const logOut = () => {
        localStorage.removeItem('expires_at');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('is_admin');
        localStorage.removeItem('language');
        localStorage.removeItem('autoLogin');
        trackSetAuthenticated(false);
        setIsAdmin(false);
        setIsAutoLogin(false);
    }

    const checkAuthentication = () => {
        trackSetAuthenticated(isTokenValid());
        setIsAdmin(isUserAdmin())
        setIsAutoLogin(isAuthAutoLogin());
    }

    return <AuthenticationContext.Provider value={{
        authenticated,
        isLoading,
        authenticate,
        authenticatePasswordless,
        checkAuthentication,
        logOut,
        register,
        isAdmin,
        isAutoLogin,
        setAdmin
    }}>{children}</AuthenticationContext.Provider>;
}



